import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../lib/msal';
import { readLocalBridge } from '../lib/localMode';

// ── Mock auth ─────────────────────────────────────────────────────────────────
//
// When VITE_MOCK_AUTH=true (set in .env.local during development), MSAL is
// bypassed entirely — no Azure AD app registration required.
// Flip this flag off once the real AAD app is registered.

/**
 * Read at call time, not captured in a module constant: a constant is evaluated once at import,
 * which makes the mock and real paths untestable in the same run (and silently pins whichever mode
 * the test environment happened to have).
 */
function isMockAuth(): boolean {
  return import.meta.env.VITE_MOCK_AUTH === 'true'
}

const MOCK_ACCOUNT: AccountInfo = {
  homeAccountId:  'dev-home-account-id',
  environment:    'login.microsoftonline.com',
  tenantId:       'dev-tenant-id',
  username:       'dev@stratuscarta.com',
  localAccountId: 'dev-local-account-id',
  name:           'Dev User',
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth() {
  // EVERY hook this function calls must run before the early returns below, unconditionally and in a
  // fixed order — not just useMsal. The desktop and mock branches return before the MSAL logic, so a
  // hook placed after them is called on some renders and not others, and React's hook order is
  // positional: the next render reads the wrong slot and throws "Rendered more hooks than during the
  // previous render".
  //
  // That is not hypothetical here. `readLocalBridge()` reads `window.stride` at RUNTIME, so a desktop
  // build whose preload lands after the first render flips the branch mid-life. useState used to sit
  // further down, past both returns.
  //
  // In mock and local modes the MSAL result is used only to satisfy the call requirement.
  const msal = useMsal()
  const [loginError, setLoginError] = useState<string | null>(null)

  // ── Desktop app ──
  //
  // Checked BEFORE mock auth, for the same reason apiClient checks it first: this is a property of
  // the host, not of how the bundle was built, and the desktop app runs the same build as the hosted
  // instance. There is no tenant to sign in to and nothing to sign out of — the session lasts
  // exactly as long as the application is open, and closing the window ends it.
  const bridge = readLocalBridge()
  if (bridge) {
    return {
      account: {
        homeAccountId:  'stride-local',
        environment:    'local',
        tenantId:       'local',
        username:       bridge.user,
        localAccountId: 'stride-local',
        name:           'Local user',
      } satisfies AccountInfo as AccountInfo,
      isAuthenticated: true,
      settled:         true,
      login:           () => void 0,
      logout:          () => void 0,
      loginError:      null as string | null,
    }
  }

  if (isMockAuth()) {
    return {
      account:         MOCK_ACCOUNT,
      isAuthenticated: true,
      // `settled` mirrors the real shape: mock auth has nothing in flight, ever.
      settled:         true,
      login:           () => void 0,
      logout:          () => console.log('[mock-auth] sign out'),
      loginError:      null as string | null,
    }
  }

  const { instance, accounts, inProgress } = msal
  const account         = accounts[0] ?? null
  const isAuthenticated = !!account

  /**
   * True once MSAL has finished whatever it was doing — startup, handling a redirect return, or an
   * interactive flow. Until then "no account" does not mean "not signed in", it means "not known
   * yet", and acting on it starts a sign-in that MSAL will reject.
   */
  const settled = inProgress === InteractionStatus.None

  function login() {
    // MSAL permits ONE interaction at a time and rejects a second with `interaction_in_progress`.
    // The previous version fired on every render where `accounts[0]` was absent — including during
    // startup and while a redirect return was still being processed — and discarded the rejection
    // with a bare `void`. The result was a user stranded on "Redirecting to login…" with nothing but
    // an unhandled promise rejection to show why.
    if (!settled) return
    instance.loginRedirect(loginRequest).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err)
      // A concurrent-interaction rejection is benign — something else is already signing in, so it
      // resolves itself. Anything else is a real failure the user has to be told about, because the
      // alternative is a blank "redirecting" screen forever.
      if (/interaction_in_progress/i.test(message)) return
      setLoginError(message)
    })
  }

  function logout() {
    void instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    })
  }

  return { account, isAuthenticated, settled, login, logout, loginError }
}
